import React, { forwardRef } from 'react';
import cn from 'classnames';
import { Link, LinkProps } from '@tanstack/react-router';
import { IconLoader } from 'components/UI/Icons/Loader';
import { IconExternalLink } from './Icons/ExternalLink';

export type FigmaButtonVariant =
  | 'default-primary'
  | 'destructive-primary'
  | 'default-secondary'
  | 'destructive-secondary'
  | 'default-tertiary'
  | 'destructive-tertiary'
  | 'default-quaternary'
  | 'destructive-quaternary'
  | 'default-link'
  | 'destructive-link';

export interface FigmaButtonProps {
  children?: React.ReactNode;
  variant?: FigmaButtonVariant;
  iconStart?: React.ReactNode;
  iconEnd?: React.ReactNode;
  badge?: string | number | React.ReactNode;
  disabled?: boolean;
  isLoading?: boolean;
  onClick?: React.ButtonHTMLAttributes<HTMLButtonElement>['onClick'];
  className?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
  size?: 16 | 20 | 24 | 26 | 32 | 40 | 48;
  to?: LinkProps['to'];
  params?: LinkProps['params'];
  target?: LinkProps['target'];
  search?: LinkProps['search'];
  hidden?: boolean;
  as?: React.ElementType;
  // PostHog autocapture identifiers (e.g. data-ph-capture-attribute-form-submit)
  // are forwarded to the underlying DOM node via ...rest.
  [dataPhCaptureAttribute: `data-ph-capture-attribute-${string}`]:
    | string
    | undefined;
}

export const FigmaButton = forwardRef<HTMLButtonElement, FigmaButtonProps>(
  (props, ref) => {
    const {
      children,
      variant = 'default-primary',
      iconStart,
      iconEnd,
      badge,
      disabled = false,
      isLoading = false,
      onClick,
      className,
      type = 'button',
      size = 32,
      to,
      params,
      target,
      search,
      hidden = false,
      as = 'button',
      ...rest
    } = props;
    if (hidden) return null;

    // Icon-only if there is an icon and no children
    const isIconOnly = (iconStart || iconEnd) && !children && !badge;

    // Badge logic
    const isBadge = badge !== undefined;
    const isBadgeComponent = !(
      typeof badge === 'string' || typeof badge === 'number'
    );

    // Main style logic, matching Button.tsx for the supported variants
    const base =
      'group inline-flex flex-shrink-0 items-center border border-solid shadow-none outline-none transition duration-200 focus-visible:outline-offset-0 font-semibold';
    const disabledStyles = 'cursor-not-allowed opacity-40';
    const enabledStyles = 'cursor-pointer';

    // Variant classes (matching Button.tsx)
    const variantClasses: Record<FigmaButtonVariant, string> = {
      'default-primary':
        'border-transparent bg-blue-500 text-gray-200 focus-visible:border-blue-1100 focus-visible:!outline-1 focus-visible:outline-blue-1100 hover:text-gray-200 active:border-blue-1100 active:text-gray-200',
      'destructive-primary':
        'border-transparent bg-red-700 text-gray-200 focus-visible:border-red-600 focus-visible:!outline-1 focus-visible:outline-red-600 hover:text-gray-200 active:border-red-600 active:text-gray-200',
      'default-secondary':
        'border-transparent bg-blue-500/8 text-blue-500 focus-visible:border-blue-500 focus-visible:bg-blue-500/16 focus-visible:!outline-1 focus-visible:outline-blue-500 hover:bg-blue-500/12 hover:text-blue-500 active:border-blue-1100 active:bg-blue-500/16',
      'destructive-secondary':
        'border-transparent bg-red-700/20 text-red-700 focus-visible:border-red-600 focus-visible:bg-red-700/20 focus-visible:!outline-1 focus-visible:outline-red-600 hover:bg-red-700/24 active:border-red-600 active:bg-red-700/28',
      'default-tertiary':
        'border-blue-500 bg-transparent text-blue-500 focus-visible:border-blue-500 focus-visible:bg-blue-50 focus-visible:!outline-1 focus-visible:outline-blue-500 hover:bg-blue-50 hover:text-blue-500 focus:border-blue-500 focus:bg-blue-100 active:border-blue-500 active:bg-blue-100',
      'destructive-tertiary':
        'border-red-700 bg-transparent text-red-700 focus-visible:border-red-600 focus-visible:!outline-1 focus-visible:outline-red-600 hover:bg-red-700/8 active:border-red-700 active:bg-red-700/12',
      'default-quaternary':
        'border-transparent bg-transparent text-blue-500 focus-visible:!outline-1 focus-visible:outline-blue-500 hover:bg-blue-50 hover:text-blue-500 focus:text-blue-500 active:text-blue-500',
      'destructive-quaternary':
        'border-transparent bg-transparent text-red-700 focus-visible:!outline-1 focus-visible:outline-red-700 hover:text-red-700 focus:text-red-700 active:text-red-700',
      'default-link':
        'border-transparent text-blue-500 hover:text-blue-500 hover:underline focus:text-blue-500 focus:underline active:text-blue-500 active:underline',
      'destructive-link':
        'border-transparent text-red-700 hover:text-red-700 hover:underline focus:text-red-700 focus:underline active:text-red-700 active:underline',
    };

    const isLinkVariant =
      variant === 'default-link' || variant === 'destructive-link';

    const borderRadiusClasses = cn({
      'rounded-[4px]': isIconOnly,
      'rounded-[8px]': !isIconOnly,
    });

    const sizeClasses = cn(
      'gap-x-1.5',
      isLinkVariant
        ? {}
        : {
            // Height, font
            'h-6 text-14/100% font-semibold': size === 24,
            'h-[26px] text-12/100% font-semibold': size === 26,
            'h-8 text-14/100% font-semibold': size === 32,
            'h-10 text-16/100% font-semibold': size === 40,
            'h-12 text-18/100% font-semibold': size === 48,
            // Icon-only padding
            'px-1': size === 26 && isIconOnly,
            'px-1.5': size === 32 && isIconOnly,
            'px-[7px]': size === 40 && isIconOnly,
            'px-[9px]': size === 48 && isIconOnly,
            // Regular padding
            'px-3': size === 26 && !isIconOnly,
            'px-4': (size === 32 || size === 40) && !isIconOnly,
            'px-5': size === 48 && !isIconOnly,
          },
    );

    // Icon size logic (for svg children)
    const iconSizeClasses = cn({
      '[&>svg>path]:stroke-2 [&>svg]:h-4 [&>svg]:w-4':
        (size === 26 || size === 16) && isIconOnly,
      '[&>svg>path]:stroke-2 [&>svg]:h-6 [&>svg]:w-6':
        (size === 24 || size === 40) && isIconOnly,
      '[&>svg>path]:stroke-2 [&>svg]:h-[18px] [&>svg]:w-[18px]':
        size === 32 && isIconOnly,
      '[&>svg>path]:stroke-2 [&>svg]:h-[28px] [&>svg]:w-[28px]':
        size === 48 && isIconOnly,
      '[&>svg>path]:stroke-3 [&>svg]:h-3 [&>svg]:w-3':
        (size === 26 || size === 24 || size === 32) && !isIconOnly,
      '[&>svg>path]:stroke-3 [&>svg]:h-[18px] [&>svg]:w-[18px]':
        (size === 40 || size === 48) && !isIconOnly,
      '[&>svg:first-of-type]:animate-spin': isLoading,
    });

    // Badge style logic (matching Button.tsx for supported variants and sizes)
    const badgeSpanClass = (isBadgeComponent: boolean) =>
      cn(
        !isBadgeComponent
          ? [
              'flex items-center justify-center rounded-[14px] font-semibold transition duration-200',
              {
                'bg-blue-500 text-white':
                  variant === 'default-secondary' ||
                  variant === 'default-tertiary' ||
                  variant === 'default-quaternary',
                'bg-red-700 text-white':
                  variant === 'destructive-secondary' ||
                  variant === 'destructive-tertiary',
                'bg-white text-blue-500': variant === 'default-primary',
                'bg-white text-gray-900': variant === 'destructive-primary',
                'h-3.5 min-w-3.5 px-1.5 text-10/100%': size === 26,
                'h-5 min-w-5 px-1.5 text-12/100%': size === 32,
                'h-[22px] min-w-[22px] px-2 text-12/100%':
                  size === 40 || size === 48,
              },
            ]
          : [
              'flex items-center justify-center',
              {
                'ms-1 h-5': size === 26,
                'ms-1 h-[22px]': size === 32 || size === 40,
              },
            ],
      );

    const showIconEnd = Boolean(iconEnd ?? target === '_blank');

    const sharedProps = {
      className: cn(
        base,
        variantClasses[variant],
        disabled || isLoading ? disabledStyles : enabledStyles,
        borderRadiusClasses,
        sizeClasses,
        iconSizeClasses,
        className,
      ),
      disabled: disabled || isLoading,
      children: (
        <>
          {/* Show spinner in place of iconStart or children when loading */}
          {isLoading ? (
            <span className={cn(iconSizeClasses, 'mr-2 flex items-center')}>
              <IconLoader />
            </span>
          ) : (
            iconStart && (
              <span className={cn(iconSizeClasses, 'flex items-center')}>
                {iconStart}
              </span>
            )
          )}
          {/* Show spinner in place of children if loading, else children */}
          {isLoading ? null : children && <span>{children}</span>}
          {/* Render badge if present */}
          {isBadge && !isBadgeComponent && (
            <span className={badgeSpanClass(false)}>{badge}</span>
          )}
          {isBadge && isBadgeComponent && (
            <span className={badgeSpanClass(true)}>{badge}</span>
          )}
          {showIconEnd && !isLoading && (
            <span
              className={cn(iconSizeClasses, 'ml-2 flex items-center', {
                'opacity-0 group-hover:opacity-100':
                  !iconEnd && target === '_blank',
              })}
            >
              {iconEnd ?? (target === '_blank' && <IconExternalLink />)}
            </span>
          )}
        </>
      ),
      // ref,
      ...rest,
    };

    if (to) {
      return (
        <Link
          to={to}
          params={params}
          target={target}
          search={search}
          {...sharedProps}
        />
      );
    }

    const Tag = as;
    return <Tag type={type} ref={ref} onClick={onClick} {...sharedProps} />;
  },
);
